import { RouterServiceTypes } from '@/services/routerService/serviceTypes'
import { algo, lib, MD5 } from 'crypto-js'
import numeral from 'numeral'
import pinyin from 'pinyin'
import { v4 as uuidV4 } from 'uuid'

type InputValue = string | number | null | undefined
const SALES_CODE_STORAGE_KEY = 'slug_code'

class CommonUtils {
  getStaticFile(name: string) {
    return require(`@/assets/${name}`)
  }

  getOsType(): 'OSX' | 'IOS' | 'Windows' | 'Linux' | 'Android' {
    const userAgent = navigator.userAgent
    const platform = (navigator as any).userAgentData?.platform || navigator.platform
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K', 'macOS']
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE']
    const iosPlatforms = ['iPhone', 'iPad', 'iPod']

    if (macosPlatforms.indexOf(platform) !== -1) {
      return 'OSX'
    }
    if (iosPlatforms.indexOf(platform) !== -1) {
      return 'IOS'
    }
    if (windowsPlatforms.indexOf(platform) !== -1) {
      return 'Windows'
    }
    if (/Android/.test(userAgent)) {
      return 'Android'
    }
    if (/Linux/.test(platform)) {
      return 'Linux'
    }
    console.error('unable to detect os type, use Windows as default', platform, userAgent)
    return 'Windows'
  }

  isWechat() {
    const ua = navigator.userAgent.toLowerCase()
    const isWXWork = ua.match(/wxwork/i)
    const isWeixin = !isWXWork && ua.match(/micromessenger/i)
    return isWeixin
  }

  /**
   * 检测是否为安卓微信环境
   * @returns boolean
   */
  isAndroidWechat(): boolean {
    const osType = this.getOsType()
    const isWechat = this.isWechat()
    return osType === 'Android' && !!isWechat
  }

  isMobile() {
    const ua = navigator.userAgent
    // MI 会误判 Mac 微信浏览器，所以这里需要特殊处理
    if (ua.includes('MacWechat') || ua.includes('UnifiedPCMacWechat')) {
      return false
    }
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)
  }

  getPayType():string{
    if(!this.checkIsMobile()){
      //  pc端
      return 'native'
    } else{
        console.log(this.isWechat(),'111')
        if(this.isWechat()){
          return 'jsapi'
        }else{
          return  'mweb'
        }
    }
  }

  checkIsMobile(){
    const ua = navigator.userAgent
    // MI 会误判 Mac 微信浏览器，所以这里需要特殊处理
    if (ua.includes('MacWechat') || ua.includes('UnifiedPCMacWechat')|| ua.includes('WindowsWechat')) {
      return false
    }
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|MI|XiaoMi|Redmi/i.test(ua)
  }
  /**
   * check if browser is chrome
   * 注意这个方法并不是完全可靠，在一些情况下edge会伪装自己的userAgent，使其和chrome一致，具体条件不明
   */
  isChrome() {
    const userAgent = navigator.userAgent
    const isChromiumBased = userAgent.includes('Chrome') && userAgent.includes('Safari')
    const isEdge = userAgent.includes('Edg')
    const isOpera = userAgent.includes('OPR')

    return isChromiumBased && !isEdge && !isOpera
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isPromise(obj: any): obj is Promise<unknown> {
    return !!obj && (typeof obj === 'object' || typeof obj === 'function') && typeof obj.then === 'function'
  }

  /** 按照指定 query 参数构建 url */
  genUrl(base: string, query: { [key: string]: string } = {}, encode = true): string {
    const keys = Object.keys(query)
    if (keys.length === 0) {
      return base
    }
    let url = base
    if (/:[a-zA-Z]/.test(base)) {
      // eslint-disable-next-line no-param-reassign
      // 处理 pattern 中声明的 restful 风格参数
      const patternParts = base.split('/')
      patternParts.forEach((p) => {
        if (p.startsWith(':')) {
          const name = p.slice(1)
          url = url.replace(p, query[name])
          delete query[name]
        }
      })
    }
    url += '?'
    for (const k of keys) {
      let v = query[k]
      if (!v) {
        continue
      }
      v = encode ? encodeURIComponent(v) : v
      url += `${k}=${v}&`
    }
    return url.slice(0, -1) // cut off last '&' or '?'
  }

  getStoredSalesCode(): string {
    try {
      return (
        window.sessionStorage.getItem(SALES_CODE_STORAGE_KEY) ||
        // window.localStorage.getItem(SALES_CODE_STORAGE_KEY) ||
        ''
      )
    } catch (_error) {
      return ''
    }
  }

  getCurrentSalesCode(): string {
    const { slug_code: slugCode = '' } = this.getUrlArgs()
    return slugCode || this.getStoredSalesCode()
  }

  buildUrlWithMergedQuery(
    base: string,
    query: { [key: string]: InputValue } = {},
    options: { keepSalesCode?: boolean } = {}
  ): string {
    const { keepSalesCode = true } = options
    const isAbsoluteUrl = /^https?:\/\//.test(base)
    const url = new URL(base, window.location.origin)

    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return
      }
      url.searchParams.set(key, String(value))
    })

    if (keepSalesCode && !url.searchParams.get('slug_code')) {
      const slugCode = this.getCurrentSalesCode()
      if (slugCode) {
        url.searchParams.set('slug_code', slugCode)
      }
    }

    if (isAbsoluteUrl) {
      return url.toString()
    }

    return `${url.pathname}${url.search}${url.hash}`
  }

  appendSalesCode(url: string): string {
    return this.buildUrlWithMergedQuery(url)
  }

  removeQueryFromUrl(url: string) {
    return url.split('?')[0]
  }

  genId(short = false) {
    const uuid = uuidV4()
    return short ? uuid.split('-')[0] : uuid
  }

  md5(content: string) {
    return MD5(content).toString()
  }

  onDOMReady(callback: () => void) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback)
    } else {
      callback()
    }
  }

  /**
   * 将key字符串转换成驼峰方式命名（如 "someName"） 的字符串
   * @param key string类型
   * @param separators key分隔符 "-"中划线/"_"下划线
   */
  camelizeKey(key: string, separators: string[] = ['-', '_']): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const out: any = []
    let i = 0
    const separatorsSet = new Set(separators)
    while (i < key.length) {
      if (separatorsSet.has(key[i])) {
        out.push(key[i + 1].toUpperCase())
        i++
      } else {
        out.push(key[i])
      }
      i++
    }
    return out.join('')
  }

  /**
   * 将对象键值对中的 key 转换为按照驼峰方式命名的 key
   * @param obj
   */
  camelize(obj: object): { [key: string]: any } {
    if (obj === null || obj === undefined) {
      return null as any
    }
    if (obj instanceof Array) {
      return obj.map((item) => {
        return this.camelize(item)
      })
    }
    if (typeof obj === 'object') {
      const out: any = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const key in obj as any) {
        const v = (obj as any)[key]
        out[this.camelizeKey(key)] = this.camelize(v)
      }
      return out
    }
    return obj
  }

  /**
   * 将key字符串转换成下划线方式命名 (如 "some_name") 的字符串
   * @param key 对象字符串
   * @param ignoreFirst 是否忽略第一个大写字母，如果忽略，会将其当成小写字母处理
   */
  underlizeKey(key: string, ignoreFirst = false): string {
    const out: string[] = []
    let i = 0
    const lowerCasedStr: string = key.toString().toLowerCase()
    while (i < key.length) {
      if (key[i] !== lowerCasedStr[i]) {
        if (!ignoreFirst || i !== 0) {
          out.push('_')
          out.push(lowerCasedStr[i])
          i++
          continue
        }
      }
      out.push(key[i].toLocaleLowerCase())
      i++
    }
    return out.join('')
  }

  /**
   * 将对象键值对中的 key 转换为按照下划线方式命名的 key
   * @param obj 需要转换的对象
   */
  underlize(obj: object): {} {
    if (obj === null || obj === undefined) {
      return null as any
    }
    if (obj instanceof Array) {
      return obj.map((item) => {
        return this.underlize(item)
      })
    }
    if (typeof obj === 'object') {
      const out: any = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const key in obj as any) {
        const v = (obj as any)[key]
        const underlizeKey = this.underlizeKey(key)
        const value = this.underlize(v)
        if (value !== null && value !== undefined) {
          out[underlizeKey] = value
        }
      }
      return out
    }
    return obj
  }

  /**
   * 将字符串转为首字母大写
   * @param str
   * @returns
   */
  capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  copyToClipboard(text: string) {
    const input = document.createElement('textarea')
    input.id = 'copy-to-clipboard'
    const container = document.body
    container.appendChild(input)
    input.oncopy = (e) => {
      e.stopPropagation()
    }
    input.value = text
    input.select()
    document.execCommand('copy')
    container.removeChild(input)
  }

  getOffsetOfPageTop(element: HTMLElement) {
    return (
      document.documentElement.clientHeight -
      (element.getBoundingClientRect().top - document.documentElement.getBoundingClientRect().top)
    )
  }

  async delay(time: number) {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve()
      }, time)
    })
  }

  formatNumber(number: InputValue) {
    if (number === undefined || number === null) {
      return '-'
    }

    const format = numeral(number).format('0,0.0')

    const key = '.0'
    const isInteger = format.includes(key)

    return isInteger ? format.replace(key, '') : format
  }

  selectFile(
    callBack: (fileList: File[]) => void,
    options?: {
      accept?: string[]
      multiple?: boolean
    }
  ) {
    const { multiple = false, accept = [] } = options || {}
    const input = document.createElement('input')

    input.type = 'file'
    if (multiple) {
      input.multiple = true
    }
    input.style.display = 'none'
    input.accept = accept
      .map((el) => {
        if (el === '.txt') {
          return 'text/plain'
        }
        return el
      })
      .join(', ')

    document.body.appendChild(input)

    const handler = () => {
      if (input.files) {
        callBack(Array.from(input.files))
      }
      input.removeEventListener('change', handler)
      input.remove()
    }

    input.addEventListener('change', handler)
    input.click()
  }

  async downloadFile(url: string) {
    // 
    const link = document.createElement('a')
    link.href = url
    document.body.appendChild(link)
    link.click()
    // 延迟移除，确保浏览器已开始处理下载
    setTimeout(() => {
      document.body.removeChild(link)
    }, 100)
  }

  /**
   * 带延迟的文件下载方法，适用于批量下载场景
   * @param url 文件URL
   * @param delay 延迟时间（毫秒），默认1500ms
   * @returns Promise，在下载触发后resolve
   */
  async downloadFileWithDelay(url: string, delay = 1500): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const link = document.createElement('a')
        link.href = url
        link.download = '' // 触发下载而非导航
        link.style.display = 'none'
        document.body.appendChild(link)
        
        // 监听可能的错误
        link.onerror = () => {
          document.body.removeChild(link)
          reject(new Error(`下载失败: ${url}`))
        }

        // 触发下载
        link.click()
        
        // 延迟更长时间确保浏览器已处理
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link)
          }
          resolve()
        }, delay)
      } catch (error) {
        reject(error)
      }
    })
  }

  getQueryFromUrl(key: string, url: string) {
    const urlObj = new URL(url)
    return urlObj.searchParams.get(key)
  }

  convertUnixToHms(num: number) {
    const h = num < 3600 ? 14 : 11
    return new Date(num * 1000).toISOString().substring(h, 19).toString()
  }

  getRealRandomNumber(range = 10) {
    const arr = new Uint32Array(1)
    window.crypto.getRandomValues(arr)
    return arr[0] % range
  }

  /**
   * 获取 url 中的参数, 以 dict 形式返回
   * @param pattern url 范式，比如 /file/:id，会自动识别其中的参数并抽取到结果 dict 中
   * @param url url，默认为当前页面 url
   */
  getUrlParams(pattern: string | null = null, url = window.location.href): { [key: string]: string } {
    let u: URL
    if (url.startsWith('http:') || url.startsWith('https:')) {
      u = new URL(url)
    } else {
      u = new URL(window.location.origin + url)
    }
    const res: { [key: string]: string } = {}
    if (pattern) {
      // 处理 pattern 中声明的 restful 风格参数
      const patternParts = pattern.split('/')
      const pathParts = u.pathname.split('/')
      patternParts.forEach((p, i) => {
        if (p.startsWith(':')) {
          const name = p.slice(1)
          res[name] = pathParts[i]
        }
      })
    }
    const params = new URLSearchParams(u.search)
    for (const k of params.keys()) {
      // params.get 会自动处理 decodeURLComponent，不需要手动调用
      const v = params.get(k)
      if (v) {
        res[k] = v
      }
    }
    return res
  }

  isInputNode(el: HTMLElement): boolean {
    if (el instanceof HTMLInputElement) {
      return true
    }
    if (el instanceof HTMLTextAreaElement) {
      return true
    }
    if (el.isContentEditable) {
      return true
    }
    return false
  }

  formatErrorMsg(err: any, defaultMsg = '未知错误，请重试或联系客服') {
    if (err instanceof Error) {
      return err.message
    }
    if (typeof err === 'string') {
      return err
    }
    if (typeof err === 'object') {
      return err.statusMessage || err.message || defaultMsg
    }
    return defaultMsg
  }

  formatFileSize(bytes: number, decimalPoint = 2) {
    if (bytes === 0) return '0 Bytes'
    const k = 1000,
      dm = decimalPoint || 2,
      sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(k))
    // eslint-disable-next-line no-restricted-properties
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
  }

  getRandomIntForRange(min: number, max: number) {
    const _min = Math.ceil(min)
    const _max = Math.floor(max)
    return Math.floor(Math.random() * (_max - _min) + _min)
  }

  getTextWidth(text: string, style: Partial<CSSStyleDeclaration>) {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) {
      return 0
    }
    const defaultFontFamily =
      style.fontFamily ||
      " -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, 'Noto Sans JP', 'Noto Sans KR', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'"
    const fontWeight = style.fontWeight || 'normal'
    context.font = `${fontWeight} ${style.fontSize} ${defaultFontFamily}`
    const metrics = context.measureText(text)

    return metrics.width
  }

  /**
   * @author mpc
   * 将项目中原本声明的非正式的 locale key 转换为国际通用的 locale key
   * 如：zh_CN -> zh-Hans
   */
  standardizeLocale(locale: string): string {
    switch (locale) {
      case 'pt_BR':
        return 'pt'
      case 'pt_PT':
        return 'pt-PT'
      case 'zh_TW':
      case 'zh_HK':
        return 'zh-Hant'
      case 'zh':
      case 'zh_CN':
        return 'zh-Hans'
      case 'es_419':
        return 'es-419'
      default:
        return locale
    }
  }

  /**
   * 生成URL链接
   * @param path URL中的路径
   * @param queryArgs URL中 “?” 后面的参数
   */
  genApiUrl(path: string, queryArgs: object, underlizeKeys = false): string {
    if (!queryArgs) {
      queryArgs = {}
    }
    if (underlizeKeys) {
      queryArgs = commonUtils.underlize(queryArgs)
    }
    const args: string[] = []
    for (const key in queryArgs as any) {
      const v = (queryArgs as any)[key]
      if (v !== null) {
        args.push(`${key}=${v}`)
      }
    }
    if (args.length === 0) {
      return path
    }
    return `${path}?${args.join('&')}`
  }

  transformNumberToKilo(num: number, fixed = 2) {
    if (num < 1000) {
      return num
    }
    return `${(num / 1000).toFixed(fixed)}k`
  }

  async getImageSize(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new window.Image()
      const objectUrl = URL.createObjectURL(file)
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
        URL.revokeObjectURL(objectUrl)
      }
      img.src = objectUrl
    })
  }

  public genUrlWithSearch(base: string) {
    return `${base}${window.location.search}`
  }

  public genUrlWithParam(base: string) {
    return `${base}&${window.location.search.substring(1)}`
  }

  // 把分转换成元
  parseCurrency(fenPrice: number, detailed = false): string {
    let yuanPrice = fenPrice / 100
    return this.numberToFixed(yuanPrice, 2, detailed)
  }

  numberToFixed(num: number, fixedLen: number, forceFixed = false): string {
    if (isNaN(num)) {
      return 'NaN'
    }
    let s = num.toString()
    let [i, f = ''] = s.split('.')
    let res = `${i}.${f.slice(0, fixedLen)}`
    if (f.length < fixedLen && forceFixed) {
      for (let i = 1; i <= fixedLen - f.length; i++) {
        res += '0'
      }
    }
    if (res[res.length - 1] === '.') {
      res = res.slice(0, -1)
    }
    return res
  }

  /** 向query中设置/添加参数，同时不会造成页面跳转，也不会增加历史记录 */
  setQueryParam<P extends RouterServiceTypes.PageType>(
    /** 此参数目中只是为了定义，没有实际作用 */
    pageName: P,
    key: keyof RouterServiceTypes.PageParams[P],
    value: string
  ) {
    const queryParams = this.getUrlParams(null, window.location.href)
    queryParams[key as string] = value
    const newUrl = this.genApiUrl(window.location.pathname, queryParams) + window.location.hash
    const lastHashState = {
      id: this.genId(),
      path: newUrl,
      createdAt: new Date(),
      wingedHistoryState: true,
    }
    window.history.replaceState(lastHashState, '', newUrl)
  }

  getUrlArgs(url?: string, camelizeParamKeys = false, allowList = false): { [k: string]: any } {
    if (!url) {
      url = window.location.href
    }
    // remove url hash
    const [href] = url.split('#')
    const s = href.split('?')[1]
    if (!s) {
      return {}
    }
    let query: {
      [key: string]: string | string[]
    } = {}
    for (const a of s.split('&')) {
      const [k, v] = a.split('=')
      let _s: string
      try {
        _s = decodeURIComponent(v)
      } catch (error) {
        _s = v
      }
      if (query[k]) {
        if (query[k] instanceof Array) {
          ;(query[k] as string[]).push(_s)
        } else {
          // query[k] is string
          if (allowList) {
            query[k] = [query[k] as string, _s]
          } else {
            continue
          }
        }
      } else {
        query[k] = _s
      }
    }
    if (camelizeParamKeys) {
      query = this.camelize(query)
    }
    return query
  }

  stringFormat(str: string, ...args: any[]) {
    return str.replace(/{(\d+)}/g, (match, number) => {
      return typeof args[number] != 'undefined' ? args[number] : match
    })
  }

  getImageView(url: string, width: number, height: number) {
    const dip = window.devicePixelRatio ?? 1
    return `${url}?imageView2/1/w/${width * dip}/h/${height * dip}`
  }

  //将中文转换为拼音
  convertToPinyin(goodsName: string) {
    const match = goodsName.match(/《(.*?)》/)
    const content = !match ? goodsName : match[1]
    const pinyinArray = pinyin(content, {
      style: pinyin.STYLE_NORMAL,
      heteronym: false,
    })

    const pinyinString = pinyinArray
      .flat()
      .join('')
      .replace(/[^a-zA-Z0-9]/g, '')
    return pinyinString
  }

  padZero(num: number, length: number = 2): string {
    return num.toString().padStart(length, '0')
  }

  replaceState(params: { delete?: string[]; add?: { [key: string]: string } }) {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams

    if (params.delete) {
      params.delete.forEach((key) => {
        searchParams.delete(key)
      })
    }
    if (params.add) {
      Object.entries(params.add).forEach(([key, value]) => {
        searchParams.set(key, value)
      })
    }
    const newUrl = url.toString()
    if (window.location.href !== newUrl) {
      window.history.replaceState(null, document.title, newUrl)
    }
  }

  once(fn: Function | null) {
    let ret: any
    return function (...args: any[]) {
      if (!fn) return ret
      ret = fn(...args)
      fn = null
      return ret
    }
  }

  getFileMd5(file: File) {
    return new Promise<string | undefined>((resolve) => {
      try {
        const chunkSize = 2097152 // 2MB
        const chunks = Math.ceil(file.size / chunkSize)
        let currentChunk = 0
        const spark = algo.MD5.create()
        const reader = new FileReader()

        reader.onload = function (e) {
          const fileContent = e.target?.result
          if (fileContent) {
            spark.update(lib.WordArray.create(fileContent as any))
            currentChunk++

            if (currentChunk < chunks) {
              loadNext()
            } else {
              const hash = spark.finalize().toString()
              resolve(hash)
            }
          } else {
            resolve(undefined)
          }
        }

        reader.onerror = function () {
          resolve(undefined)
        }

        const loadNext = () => {
          const start = currentChunk * chunkSize
          const end = start + chunkSize >= file.size ? file.size : start + chunkSize
          reader.readAsArrayBuffer(file.slice(start, end))
        }

        loadNext()
      } catch {
        resolve(undefined)
      }
    })
  }
}

export const commonUtils = new CommonUtils()
